import React from 'react';

import { useTranslation } from '@modernloop/i18n';
import { DateTimeRangePicker } from '@modernloop/shared/components';
import {
  IsoTimestamp,
  assertIsoTimestamp,
  ensureEndWithin24Hours,
  getAvailabilityMinutes,
  getHoursAndMinutesString,
} from '@modernloop/shared/datetime';
import { TrashIcon } from '@modernloop/shared/icons';
import { Box, IconButton, Stack, Typography } from '@mui/material';

import { DateTimeRangeInput } from 'src/generated/mloop-graphql';

interface Props {
  availability: DateTimeRangeInput;
  timezone: string;
  onChange: (start: string, end: string) => void;
  onDelete: () => void;
}

export const AvailabilityContent: React.FC<Props> = ({ availability, timezone, onChange, onDelete }) => {
  const { t } = useTranslation();
  const minutes = getAvailabilityMinutes(availability.startAt, availability.endAt);
  const durationLabel =
    minutes > 0 ? getHoursAndMinutesString(minutes) : t('hiring-dashboard-schedule:schedule.availability.zeroDuration');

  const handleChange = (start: IsoTimestamp, end: IsoTimestamp) => {
    const adjusted = ensureEndWithin24Hours(start, end);
    onChange(adjusted.start, adjusted.end);
  };

  return (
    <Stack
      data-testid="candidate-details-add-candidate-availability-time-range"
      key={`${availability.startAt}-${availability.endAt}`}
      direction="row"
      gap={0.5}
    >
      <Box display="flex">
        <DateTimeRangePicker
          coalesceTimeUpdates={false}
          value={{ start: assertIsoTimestamp(availability.startAt), end: assertIsoTimestamp(availability.endAt) }}
          timezone={timezone}
          onChange={handleChange}
          disablePast
        />
      </Box>
      <Box display="flex">
        <IconButton onClick={onDelete}>
          <TrashIcon />
        </IconButton>
      </Box>
      <Box display="flex" alignItems="center" px={0.5}>
        <Typography variant="caption" data-testid="availability-row-duration-label">
          {durationLabel}
        </Typography>
      </Box>
    </Stack>
  );
};
